/**
 * Path: packages/frontend/src/core/composables/usePermissions.ts
 * Task: Centralized composable for evaluating atomic capabilities and derived access rights.
 * Update: Transformed into capability-based access control engine with backwards-compatible computed helpers.
 */
import { computed } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';

export function usePermissions() {
  const authStore = useAuthStore();
  const projectStore = useProjectStore();

  const isAppAdmin = computed<boolean>(() => authStore.isAppAdmin);
  const isAuthenticated = computed<boolean>(() => authStore.isAuthenticated);

  /**
   * Primary capability evaluation method.
   * Grants unconditional access to App Admins (God-Mode), otherwise inspects effective capabilities.
   */
  const can = (capability: string): boolean => {
    if (isAppAdmin.value) return true;
    return authStore.effectiveCapabilities.includes(capability);
  };

  const projectRole = computed<string>(() => {
    const loaded = projectStore.loadedProject?.value;
    if (!loaded || !authStore.user) return 'GUEST';
    if (isAppAdmin.value) return 'PROJECT_ADMIN';

    const meta = projectStore.projectsMetadata?.[loaded];
    if (!meta || !meta.permissions) return 'GUEST';

    const email = (authStore.user.email || '').toLowerCase();
    if ((meta.permissions.projectAdmin || []).map((e: string) => e.toLowerCase()).includes(email)) return 'PROJECT_ADMIN';
    if ((meta.permissions.author || []).map((e: string) => e.toLowerCase()).includes(email)) return 'AUTHOR';
    if ((meta.permissions.reader || []).map((e: string) => e.toLowerCase()).includes(email)) return 'READER';

    return 'GUEST';
  });

  const canEditWorkspace = computed<boolean>(() => can('workspace:edit_entity'));
  const canCreateEntity = computed<boolean>(() => can('workspace:create_entity'));
  const canDeleteEntity = computed<boolean>(() => can('workspace:delete_entity'));
  const canSortMatrix = computed<boolean>(() => can('workspace:sort_matrix'));

  const canManageProjectSettings = computed<boolean>(() => can('project:update_settings'));
  const canManageMembers = computed<boolean>(() => can('project:manage_members'));
  const canDeleteProject = computed<boolean>(() => can('project:delete'));
  const canReadProject = computed<boolean>(() => can('project:read'));
  const canCreateProject = computed<boolean>(() => can('project:create'));

  const canReadComments = computed<boolean>(() => can('comments:read'));
  const canComment = computed<boolean>(() => can('comments:create'));
  const canEditComment = computed<boolean>(() => can('comments:edit_own'));
  const canModerateComments = computed<boolean>(() => can('comments:delete_any'));

  return {
    // Primary capability checker
    can,

    // Base identity properties
    isAppAdmin,
    isAuthenticated,
    projectRole,

    // High-level derived capability helpers (Zero breaking changes for existing components)
    canEditWorkspace,
    canCreateEntity,
    canDeleteEntity,
    canSortMatrix,
    canManageProjectSettings,
    canManageMembers,
    canDeleteProject,
    canReadProject,
    canCreateProject,
    canReadComments,
    canComment,
    canEditComment,
    canModerateComments
  };
}
